import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { declineParamKey, Select } from '@aline/common';
import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlineButton, AnimatedAlineDialog, WhiteAlineButton } from 'src/components';
import { WhiteAlineLogo } from 'src/components/icons/WhiteAlineLogo';
import { ReassignFieldDialog } from 'src/editor/popovers/fields/ReassignFieldDialog';
import {
  useExitWalkthrough,
  useWalkthroughCta,
  walkthroughStateVar,
} from 'src/editor/toolbars/editor-app-bars/helpers';
import { WalkthroughOverflowMenu } from 'src/editor/toolbars/editor-app-bars/walkthrough-overflow-menu';
import { domElIdCreators, useDocumentParams } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import {
  useDeclineSignatureReminders,
  useDocumentSignatureStatus,
  useDownloadFileVersionWithLoading,
  useGetCurrentDoc,
  useGetDocumentWorkflow,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },
  dialogButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
  },
  firstButton: {
    [theme.breakpoints.down('md')]: {
      marginLeft: 'auto',
    },
  },
  logo: {
    marginLeft: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(1),
    },
  },
  spacer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  text: {
    color: theme.palette.common.white,
  },
}));

export function WalkThroughAppBarContent(): JSX.Element {
  const { classes, cx } = useStyles();
  const { documentId } = useDocumentParams();
  const { document } = useGetCurrentDoc();
  const { downloadFileVersionWithLoading } = useDownloadFileVersionWithLoading();
  const {
    buttonText,
    disabled: buttonDisabled,
    handleClick: handleAdvance,
    isRequesterAlreadyFinished,
    requesterNeedsToClickFinish,
    submitting: walkthroughSubmitting,
  } = useWalkthroughCta();
  const { exitWalkthrough } = useExitWalkthrough();
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const {
    requesterWalkthroughInfo: { requesterActionsToDo },
  } = useReactiveVar(walkthroughStateVar);
  const activeRecipient = requesterActionsToDo.find(
    a => a.__typename === 'WorkflowRecipient' && !a.completedAt
  );
  const recipientId = activeRecipient?.id ?? null;
  const { workflow } = useGetDocumentWorkflow(documentId);
  const { declineSignatureReminders, loading: decliningReminders } = useDeclineSignatureReminders(
    workflow?.id ?? null,
    documentId
  );
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const { stripDeclineParam } = useOpenDeclineModalFromUrl({
    setDeclineModalOpen,
    wasSentForSignature,
  });
  const closeDeclineModal = useCallback(() => {
    setDeclineModalOpen(false);
    stripDeclineParam();
  }, [stripDeclineParam]);
  // Order of precedence matters.
  const appBarText = isRequesterAlreadyFinished
    ? 'It looks like you already finished signing. Nice work!'
    : requesterNeedsToClickFinish
      ? `Click "Finish Signing" to complete your signature.`
      : 'Please review the document and complete assigned items.';

  function handleClickFinishLater(): void {
    exitWalkthrough();
  }

  function handleDownload(): void {
    if (!document?.latestVersion) return;
    void downloadFileVersionWithLoading(documentId, {
      exportName: document.name,
      fileVersion: 'latestVersion',
    });
  }

  return (
    <>
      <WhiteAlineLogo classes={{ root: classes.logo }} />
      {!IS_MOBILE && (
        <div className={classes.spacer}>
          <Typography className={classes.text} variant="body1">
            {appBarText}
          </Typography>
        </div>
      )}
      <WhiteAlineButton
        className={cx(classes.button, classes.firstButton)}
        data-cy={Select.document.editor.walkthrough.advanceWalkthroughButton}
        disabled={buttonDisabled}
        id={domElIdCreators.advanceWorkflowButton()}
        loading={walkthroughSubmitting}
        onClick={handleAdvance}
      >
        {buttonText}
      </WhiteAlineButton>
      <WalkthroughOverflowMenu
        canDownload={Boolean(document?.latestVersion)}
        recipientId={recipientId}
        wasSentForSignature={wasSentForSignature}
        onDecline={() => setDeclineModalOpen(true)}
        onDownload={handleDownload}
        onEdit={handleClickFinishLater}
        onEscape={handleClickFinishLater}
        onReassign={() => setIsReassignOpen(true)}
      />
      <AnimatedAlineDialog
        open={declineModalOpen}
        title={<Typography variant="h1">Decline signing</Typography>}
        onClose={closeDeclineModal}
      >
        <Typography variant="body1">
          Are you sure you want to decline signing this document? This action cannot be undone.
        </Typography>
        <div className={classes.dialogButtons}>
          <AlineButton variant="outlined" onClick={closeDeclineModal}>
            Cancel
          </AlineButton>
          <AlineButton
            color="error"
            loading={decliningReminders}
            variant="contained"
            onClick={async () => {
              await declineSignatureReminders();
              exitWalkthrough();
            }}
          >
            Confirm
          </AlineButton>
        </div>
      </AnimatedAlineDialog>
      {recipientId && (
        <ReassignFieldDialog
          open={isReassignOpen}
          recipientId={recipientId}
          onClose={() => setIsReassignOpen(false)}
        />
      )}
    </>
  );
}

/**
 * When the user lands here from a "Decline to sign" email link
 * (`?decline=true`), open the existing decline confirmation modal. Gated on
 * `wasSentForSignature` so the modal only opens for actual signers. Returns
 * `stripDeclineParam` so callers can remove the URL param at close time — the
 * strip must NOT run inside an effect chained off the open, since that races
 * `useGetStartWalkthrough`, which reads `window.location.search` synchronously
 * to decide whether to carry `decline` through the auto-redirect to /fields.
 */
function useOpenDeclineModalFromUrl({
  setDeclineModalOpen,
  wasSentForSignature,
}: {
  setDeclineModalOpen: Dispatch<SetStateAction<boolean>>;
  wasSentForSignature: boolean;
}): { stripDeclineParam: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    if (autoOpenedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get(declineParamKey) !== 'true') return;
    if (!wasSentForSignature) return;

    autoOpenedRef.current = true;
    setDeclineModalOpen(true);
  }, [location.search, setDeclineModalOpen, wasSentForSignature]);

  const stripDeclineParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(declineParamKey) !== 'true') return;
    params.delete(declineParamKey);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  return { stripDeclineParam };
}
